import { useCallback, useEffect, useRef, useState } from "react";
import { LayerInspectorSelection } from "../../helpers/layerInspectorTools";
import { resolveSelectionLayerDetails } from "../../helpers/layerInspectorPrefill";
import { buildLayerInspectorSelectionKey } from "../../helpers/layerSelectionUtils";
import { useMst } from "../../stores/main";
import { mainAssetKey } from "../../stores/PlayerStore";

type LayerSelectionOption = {
  value: string;
  children?: LayerSelectionOption[];
};

interface UseLayerInspectorSelectionArgs {
  jsonVid?: any;
  isMulti: boolean;
  selectableOptions: LayerSelectionOption[];
  selectedLayers: any[] | undefined;
  handleChange: (value: any) => void;
}

export function useLayerInspectorSelection({
  jsonVid,
  isMulti,
  selectableOptions,
  selectedLayers,
  handleChange,
}: UseLayerInspectorSelectionArgs) {
  const {
    playerStore: {
      layerInspectorAvailable,
      layerInspectorPrimarySelection,
      setLayerInspectorSelectionCaptureEnabled,
    },
  } = useMst();

  const [selectionFromPlayerEnabled, setSelectionFromPlayerEnabled] = useState(
    false
  );
  const lastInspectorSelectionKeyRef = useRef<string>("");
  const canSelectFromPlayer = layerInspectorAvailable;

  const stopSelectionCapture = useCallback(() => {
    setSelectionFromPlayerEnabled(false);
    setLayerInspectorSelectionCaptureEnabled(false);
  }, [setLayerInspectorSelectionCaptureEnabled]);

  const startSelectionCapture = useCallback(() => {
    if (!canSelectFromPlayer) return;
    lastInspectorSelectionKeyRef.current = buildLayerInspectorSelectionKey(
      layerInspectorPrimarySelection,
      mainAssetKey
    );
    setSelectionFromPlayerEnabled(true);
    setLayerInspectorSelectionCaptureEnabled(true);
  }, [
    canSelectFromPlayer,
    layerInspectorPrimarySelection,
    setLayerInspectorSelectionCaptureEnabled,
  ]);

  const applySelectionFromInspector = useCallback(
    (selection?: LayerInspectorSelection) => {
      const resolvedLayer = resolveSelectionLayerDetails(
        selection,
        jsonVid,
        mainAssetKey
      );
      const resolvedLayerUid = resolvedLayer?.layerUid;
      if (resolvedLayerUid === undefined) {
        return false;
      }

      const resolvedAssetId = resolvedLayer?.assetId;
      const selectionPath =
        findSelectionPathByLayerUid(
          selectableOptions,
          resolvedLayerUid,
          resolvedAssetId
        ) ??
        (resolvedAssetId
          ? findSelectionPathByLayerUid(selectableOptions, resolvedLayerUid)
          : undefined);
      if (!selectionPath || selectionPath.length === 0) return false;

      if (isMulti) {
        const prevSelections = selectedLayers ? [...selectedLayers] : [];

        const alreadySelected = prevSelections.some((sel) => {
          const lastSegment = getPathLeafValue(sel);
          const parsed = lastSegment
            ? getOptionValueMetadata(lastSegment)
            : undefined;
          return parsed?.layerUid === resolvedLayerUid;
        });
        if (alreadySelected) {
          return true;
        }

        handleChange([...prevSelections, selectionPath] as any[]);
      } else {
        handleChange([selectionPath] as any[]);
      }
      return true;
    },
    [handleChange, isMulti, jsonVid, selectableOptions, selectedLayers]
  );

  useEffect(() => {
    if (!selectionFromPlayerEnabled) return;
    const currentSelectionKey = buildLayerInspectorSelectionKey(
      layerInspectorPrimarySelection,
      mainAssetKey
    );
    if (
      !layerInspectorPrimarySelection ||
      lastInspectorSelectionKeyRef.current === currentSelectionKey
    ) {
      return;
    }
    const applied = applySelectionFromInspector(layerInspectorPrimarySelection);
    if (applied) {
      lastInspectorSelectionKeyRef.current = currentSelectionKey;
      stopSelectionCapture();
    }
  }, [
    applySelectionFromInspector,
    layerInspectorPrimarySelection,
    selectionFromPlayerEnabled,
    stopSelectionCapture,
  ]);

  useEffect(() => {
    if (!canSelectFromPlayer && selectionFromPlayerEnabled) {
      stopSelectionCapture();
    }
  }, [
    canSelectFromPlayer,
    selectionFromPlayerEnabled,
    stopSelectionCapture,
  ]);

  useEffect(() => {
    return () => {
      if (selectionFromPlayerEnabled) {
        stopSelectionCapture();
      }
    };
  }, [selectionFromPlayerEnabled, stopSelectionCapture]);

  return {
    layerInspectorAvailable,
    canSelectFromPlayer,
    selectionFromPlayerEnabled,
    startSelectionCapture,
    stopSelectionCapture,
  };
}

function findSelectionPathByLayerUid(
  options: LayerSelectionOption[],
  targetLayerUid: number,
  targetAssetId?: string
): string[] | undefined {
  for (const option of options) {
    const layerDetails = getOptionValueMetadata(option.value);
    const matchesAsset =
      targetAssetId === undefined ||
      (typeof layerDetails?.assetId === "string" &&
        layerDetails.assetId === targetAssetId);
    if (layerDetails?.layerUid === targetLayerUid && matchesAsset) {
      return [option.value];
    }
    if (option.children?.length) {
      const childPath = findSelectionPathByLayerUid(
        option.children,
        targetLayerUid,
        targetAssetId
      );
      if (childPath) {
        return [option.value, ...childPath];
      }
    }
  }
  return undefined;
}

function getOptionValueMetadata(value?: string) {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function getPathLeafValue(path: any) {
  if (Array.isArray(path)) {
    return path.length ? path[path.length - 1] : undefined;
  }
  return path;
}
